<template>
  <div>
    <RepruAuswahlModal
      :visible.sync="showReiseterminFilterModal"
      :is-initial-load="isInitialLoad"
      :selected-reisetermin="selectedReisetermin"
      :selected-individualreisen="selectedIndividualreisen"
      :page="page"
      @anwenden="onFilterAnwenden"
    />

    <FilterOverview :showFilterInitially="false" title="Rechnungsprüfung" @reload="onReload">
      <template v-if="!isInitialLoad" #toolbar-left>
        <RepruToolbarFilter
          :all-rows="allUnfilteredRows"
          :filter-values="filterValues"
          :comments="selectedReisetermin?.comments"
          @change="onToolbarFilterChange"
          @reset="onFilterReset"
          :page="page"
        />
      </template>

      <template v-if="!isInitialLoad" #toolbar-right>
        <div class="d-flex">
          <div
            class="icon-button d-flex justify-content-center mr-4"
            @click="openFilterModal"
            v-b-tooltip.hover.bottom
            :title="page === 'fit' ? 'Reise bearbeiten' : 'Reisetermin bearbeiten'"
          >
            <span class="d-flex align-items-center"><Pencil :size="20" /></span>
          </div>
          <div
            class="icon-button d-flex justify-content-center mr-4"
            @click="multipleLeistungenFreigeben"
            v-b-tooltip.hover.bottom
            :title="'Freigeben'"
            v-if="leistungenSindFreigebbar && showMultiSelectedActionAvailable"
          >
            <span class="d-flex align-items-center"><FileCheck :size="20" /></span>
          </div>

          <div
            class="icon-button d-flex justify-content-center mr-4"
            @click="multipleLeistungenFreigabeZuruecknehmen"
            v-b-tooltip.hover.bottom
            :title="'Freigabe zurücknehmen'"
            v-if="leistungenSindZuruecknehmbar && showMultiSelectedActionAvailable"
          >
            <span class="d-flex align-items-center"><RotateCcw :size="20" /></span>
          </div>

          <div
            class="icon-button d-flex justify-content-center mr-4"
            @click="openAufgabenModalWithData"
            v-b-tooltip.hover.bottom
            :title="'Aufgabe erstellen'"
          >
            <span class="d-flex align-items-center"><ListChecks :size="20" /></span>
          </div>
        </div>
      </template>

      <template v-if="!isInitialLoad" #table="{ tableHeight }">
        <AgGridVue
          :style="{ height: tableHeight + 87 + 'px' }"
          rowHeight="50px"
          class="ag-theme-alpine m-0 p-0"
          :columnDefs="columnDefs"
          :defaultColDef="defaultColDef"
          :rowModelType="'serverSide'"
          :rowSelection="'multiple'"
          suppressCellFocus
          @rowSelected="onRowSelected"
          @grid-ready="onGridReady"
          @cell-editing-stopped="stornoFreigabebetragBearbeiten"
          @cellClicked="selectRowOnCellClicked"
          suppressRowClickSelection
          :getRowId="getRowId"
          :enableCellTextSelection="true"
          :tooltipShowDelay="0"
          suppressContextMenu
          :statusBar="statusBar"
          :animateRows="true"
          :getRowClass="getRowClass"
          :isRowSelectable="isRowSelectable"
        ></AgGridVue>

        <AufgabeErstellenModal
          :prefixedVerlinkungen="prefixedVerlinkungen"
          :disableAttachments="true"
          :enableManualTask="true"
          :fromRepru="true"
        />
      </template>
    </FilterOverview>

    <RepruLeistungFreigabeModal
      :visible.sync="showFreigabeModal"
      :selectedLeistungen="selectedLeistungenForFreigabe"
      :isSaving="isFreigabeSaving"
      @freigeben="handleFreigabe"
    />

    <RepruLeistungFreigabeZuruecknehmenModal
      :visible.sync="showFreigabeZuruecknehmenModal"
      :selectedLeistungen="selectedLeistungenForFreigabeZuruecknehmen"
      @freigabeZuruecknehmen="handleFreigabeZuruecknehmen"
    ></RepruLeistungFreigabeZuruecknehmenModal>

    <RepruLeistungFreigabeModal
      :visible.sync="showErneutFreigabeModal"
      :selectedLeistungen="selectedLeistungenForErneutFreigabe"
      title="Freigabe anpassen"
      confirmLabel="Freigabe anpassen"
      modalId="modal-repru-leistung-erneut-freigabe"
      mode="anpassen"
      :isSaving="isFreigabeSaving"
      @freigeben="handleErneutFreigabe"
    />
  </div>
</template>

<script>
import FilterOverview from '@/components/common/filter-overview.vue';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridVue } from 'ag-grid-vue';
import 'ag-grid-enterprise';
import CellRendererAktionenKatalogReisen from '@/components/produkte/reisetermine/cell-renderer-aktionen-katalog-reisen.vue';
import CellRendererRepruStatus from '@/components/vorgaenge/cell-renderer-repru-status.vue';
import CellRendererAnzahlLeistungen from '@/components/vorgaenge/cell-renderer-anzahl-leistungen.vue';
import CellRendererLeistungskuerzelStorno from '@/components/vorgaenge/cell-renderer-leistungskuerzel-storno.vue';
import AufgabeErstellenModal from '@/components/aufgaben/aufgabe-erstellen-modal.vue';
import RepruAuswahlModal from '@/components/vorgaenge/repru-auswahl-modal.vue';
import RepruLeistungFreigabeModal from '@/components/vorgaenge/repru-leistung-freigabe-modal.vue';
import RepruLeistungFreigabeZuruecknehmenModal from '@/components/vorgaenge/repru-leistung-freigabe-zuruecknehmen-modal.vue';
import RepruToolbarFilter from '@/components/vorgaenge/repru-toolbar-filter.vue';
import { getFreigabegrundLabel } from '@/components/vorgaenge/repru-freigabegrund.constants';
import {
  STORNIERTE_BLANK_STATES,
  istVorgangStorniert,
  istStornozeile,
  istNurLesbar,
} from '@/components/vorgaenge/repru-storno.constants';
import RepruStatusBar from '@/components/vorgaenge/repru-status-bar.vue';
import CustomTooltip from '@/components/flugverfuegbarkeit/custom-tooltip.vue';

import { FileCheck, RotateCcw, Pencil, ListChecks, CircleCheck } from 'lucide-vue';
import { lucideHtml } from '@/core/common/helpers/lucide-html';
import * as odataService from '@/core/common/services/odata.service';
import apiService from '@/core/common/services/api.service';
import { format, parseISO } from 'date-fns';
import {
  formatDateWithoutHours,
  formatRechnungsnummern,
  parseFullUsername,
} from '@/core/common/helpers/utils.js';
import {
  buildSubRowsFor,
  hasExpandableSubRows,
  buildDisplayedLeistungen,
  toggleExpandedId,
} from '@/core/common/helpers/teilbetraege-sub-rows.js';
import { mapGetters, mapState } from 'vuex';
import currencies from '@/core/common/helpers/currencies.json';

// StorniertOhneRechnung ergänzt, damit nach der Freigabe stornierte Vorgänge sichtbar bleiben
const BUCHUNG_BLANK_STATES = ['Buchung', ...STORNIERTE_BLANK_STATES];

export default {
  components: {
    ListChecks,
    FilterOverview,
    AgGridVue,
    FileCheck,
    RotateCcw,
    Pencil,
    CellRendererAktionenKatalogReisen,
    CellRendererRepruStatus,
    CellRendererAnzahlLeistungen,
    CellRendererLeistungskuerzelStorno,
    AufgabeErstellenModal,
    RepruAuswahlModal,
    RepruLeistungFreigabeModal,
    RepruLeistungFreigabeZuruecknehmenModal,
    RepruToolbarFilter,
    RepruStatusBar,
    CustomTooltip,
  },
  props: {
    page: {
      type: String,
      required: true,
      validator: value => ['katalog', 'fit'].includes(value),
    },
  },
  data() {
    return {
      gridApi: null,
      gridColumnApi: null,
      columnDefs: null,
      expandedLeistungIds: new Set(),
      mainLeistungenRows: [],
      statusBar: {
        statusPanels: [{ statusPanel: 'RepruStatusBar', key: 'repruStatusBar', align: 'left' }],
      },
      defaultColDef: {
        resizable: false,
        suppressMenu: true,
        sortable: false,
        flex: 1,
        floatingFilterComponentParams: {
          suppressFilterButton: true,
        },
        suppressMovable: true,
        filterParams: {
          defaultToNothingSelected: true,
        },
        tooltipComponent: 'CustomTooltip',
        // Sub-Zeilen bekommen einen hellblauen Hintergrund,
        // wenn die zugehörige Eltern-Leistung selektiert ist
        cellClassRules: {
          'leistungen-overview__sub-cell--main-selected': params => {
            if (!params.data?.isSubRow) return false;
            const mainNode = params.api.getRowNode(`${params.data.mainLeistungId}`);
            return mainNode?.isSelected() === true;
          },
        },
      },
      showReiseterminFilterModal: true,
      isInitialLoad: true,
      // Katalog Page
      selectedReisetermin: null,
      // FIT Page
      filterValues: {
        kreditor: null,
        leistungskuerzel: null,
        teilleistungskuerzel: null,
        bnr: null,
        rechnungsnummer: null,
        tvzBuchen: null,
      },
      allUnfilteredRows: [],
      prefixedVerlinkungen: [],
      allSelectedRows: [],
      selectedRowIds: null,
      appliedFilterValues: null,
      currentDisplayedRows: [],
      showFreigabeModal: false,
      showFreigabeZuruecknehmenModal: false,
      showErneutFreigabeModal: false,
      isFreigabeSaving: false,
      selectedLeistungenForFreigabe: [],
      selectedLeistungenForFreigabeZuruecknehmen: [],
      selectedLeistungenForErneutFreigabe: [],
      selectedIndividualreisen: [],
    };
  },
  created() {
    this.columnDefs = [
      {
        field: 'bnr',
        headerName: 'BNR',
        filter: 'agTextColumnFilter',
        checkboxSelection: params => !params.data?.isSubRow && !istNurLesbar(params.data),
        headerCheckboxSelection: true,
        pinned: 'left',
        minWidth: 120,
        width: 120,
      },
      {
        field: 'betrag',
        headerName: 'Gruppenbetrag',
        headerClass: 'ag-header-center',
        pinned: 'left',
        minWidth: 140,
        width: 140,
        cellStyle: params => {
          const isStornoOrTeilstorno =
            params.data?.leistungstyp === 'Storno' || params.data?.leistungstyp === 'Teilstorno';
          const isEditable = isStornoOrTeilstorno && !params.data?.isFreigegeben;
          return {
            textAlign: 'right',
            cursor: isEditable ? 'pointer' : 'default',
            alignContent: 'center',
          };
        },
        cellRenderer: params => {
          if (params.data?.leistungstyp === 'Storno' || params.data?.leistungstyp === 'Teilstorno') {
            const betrag = params.data.betrag ?? 0;
            const formattedValue = betrag.toLocaleString('de-DE', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
            return `<b>${formattedValue} ${params.data.waehrung ?? ''}</b>`;
          }
          if (!params.value) return '-';
          const formattedValue = params.value.toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          return `<b>${formattedValue} ${params.data.waehrung} </b>`;
        },
        editable: params => {
          const isStornoOrTeilstorno =
            params.data?.leistungstyp === 'Storno' || params.data?.leistungstyp === 'Teilstorno';
          return isStornoOrTeilstorno && !params.data?.isFreigegeben;
        },
        valueSetter: params => {
          const input = String(params.newValue).trim();
          //gruppiert die eingabe in 2 Gruppen (Betrag und Währung) - Prüft das Muster der eingabe
          const match = input.match(/^(-?[0-9.,]+)\s*([a-zA-Z]{3})?$/);
          if (!match) return false;

          // Wandelt den Betrag in eine zahl mit Punkt um
          const neuerBetrag = parseFloat(match[1].replace(',', '.'));
          if (isNaN(neuerBetrag)) return false;

          // check auf Code der currencies
          const waehrungInput = match[2]?.toUpperCase();
          if (waehrungInput) {
            if (!currencies[waehrungInput]) return false;
            params.data.waehrung = waehrungInput;
          }

          params.data.betrag = neuerBetrag;
          return true;
        },
        valueGetter: params => {
          if (params.data?.leistungstyp === 'Storno' || params.data?.leistungstyp === 'Teilstorno') {
            return params.data.betrag ?? 0;
          }
          return params.data?.betrag;
        },
      },
      {
        field: 'anzahlLeistungen',
        headerName: 'Anzahl Leistungen',
        cellRenderer: 'CellRendererAnzahlLeistungen',
        cellRendererParams: {
          onToggle: leistungId => this.toggleExpand(leistungId),
        },
        pinned: 'left',
        minWidth: 160,
        width: 160,
        cellStyle: {
          display: 'flex',
          justifyContent: 'center',
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        pinned: 'left',
        minWidth: 80,
        width: 80,
        cellRendererSelector: params => {
          if (params.data?.isSubRow) return null;
          return { component: 'CellRendererRepruStatus' };
        },
        cellStyle: {
          display: 'flex',
          justifyContent: 'center',
        },
        tooltipValueGetter: params => {
          const { isFreigegeben, betrag, freigabebetrag, waehrung } = params.data ?? {};
          if (!isFreigegeben || !freigabebetrag || betrag === freigabebetrag) return null;
          return `Freigegebener Betrag: ${this.formatBetrag(freigabebetrag)} ${waehrung || ''}`;
        },
      },
      {
        field: 'kreditor',
        headerName: 'Kreditor',
        minWidth: 100,
        width: 100,
      },
      {
        field: 'npx',
        headerName: 'PAX VG',
        minWidth: 90,
        width: 90,
      },
      {
        field: 'teilleistungskuerzel',
        headerName: 'Teilleistungskürzel',
        cellRenderer: params => (params.value == null || params.value === '' ? '-' : params.value),
        filter: 'agSetColumnFilter',
        minWidth: 170,
        width: 170,
      },
      {
        field: 'leistungskuerzel',
        headerName: 'Leistungskürzel',
        filter: 'agSetColumnFilter',
        cellRendererSelector: params => {
          if (params.data?.isSubRow) return null;
          return {
            component: 'CellRendererLeistungskuerzelStorno',
            params: {
              onStornoDuplizieren: leistung => this.stornoDuplizieren(leistung),
              canDuplizieren: data => this.canStornoDuplizieren(data),
            },
          };
        },
        minWidth: 150,
        width: 150,
      },
      {
        field: 'satzart',
        headerName: 'Satzart',
        filter: 'agSetColumnFilter',
        cellRenderer: params => (params.value == null || params.value === '' ? '-' : params.value),
        minWidth: 100,
        width: 100,
      },
      {
        field: 'kunde',
        headerName: 'Kund:in',
        cellRenderer: params => {
          if (!params.data.masterKundeVorname || !params.data.masterKundeNachname) return '-';
          return `${params.data.masterKundeVorname} ${params.data.masterKundeNachname}`;
        },
        minWidth: 110,
      },
      {
        field: 'reisezeitraum',
        headerName: 'Reisezeitraum',
        cellRenderer: params => {
          if (!params.data.reisdatum || !params.data.enddatum) return '-';
          const startdatum = format(parseISO(params.data.reisdatum), 'dd.MM.yyyy');
          const enddatum = format(parseISO(params.data.enddatum), 'dd.MM.yyyy');
          return `${startdatum} - ${enddatum}`;
        },
        minWidth: 200,
        width: 200,
      },
      {
        field: 'zimmertyp',
        headerName: 'Zimmertyp',
        cellRenderer: params => (params.value == null || params.value === '' ? '-' : params.value),
        minWidth: 110,
        width: 110,
      },
      {
        field: 'rechnungsnummer',
        headerName: 'Rechnungsnummer',
        filter: 'agTextColumnFilter',
        cellRenderer: params => formatRechnungsnummern(params.data?.freigaben, params.data?.waehrung),
        tooltipValueGetter: params => {
          const formatted = formatRechnungsnummern(params.data?.freigaben, params.data?.waehrung);
          return formatted === '-' ? null : formatted;
        },
        minWidth: 180,
        width: 180,
      },
      {
        field: 'freigabebetrag',
        headerName: 'Freigabebetrag',
        valueFormatter: params => {
          if (params.value == null) return '';
          return params.value.toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        },
        cellRenderer: params => {
          if (!params.value) return '-';
          const formattedValue = params.value.toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          return `${formattedValue} ${params.data.waehrung ?? ''}`;
        },
        minWidth: 140,
        width: 140,
      },
      {
        field: 'freigabedatum',
        headerName: 'Freigabedatum',
        cellRenderer: params => (params.value ? formatDateWithoutHours(params.value) : '-'),
        minWidth: 140,
        width: 140,
      },
      {
        field: 'freigegebenVon',
        headerName: 'Freigegeben durch',
        cellRenderer: params => this.getUserNameById(params.value),
        minWidth: 170,
        width: 170,
      },
      {
        field: 'tvzBuchen',
        headerName: 'T-VZ buchen',
        cellRenderer: params => {
          return params.value ? lucideHtml(CircleCheck, { size: 20, class: 'text-success' }) : '-';
        },
        filter: 'agSetColumnFilter',
        minWidth: 120,
        width: 120,
        hide: this.page !== 'katalog',
      },
      {
        field: 'rechnungsdatum',
        headerName: 'Rechnungsdatum',
        cellRenderer: params => (params.value ? formatDateWithoutHours(params.value) : '-'),
        minWidth: 150,
        width: 150,
      },
      {
        field: 'freigabegrund',
        headerName: 'Grund',
        cellRenderer: params => getFreigabegrundLabel(params.value),
        minWidth: 200,
        width: 200,
      },
      {
        headerName: 'Aktionen',
        field: 'actions',
        cellRendererSelector: params => {
          if (params.data?.isSubRow) return null;
          return {
            component: 'CellRendererAktionenKatalogReisen',
            params: {
              onShowModalAufgabeEdit: this.onShowModalAufgabeEdit,
              getSelectedReisetermin: () => this.selectedReisetermin,
              rowFreigeben: this.rowFreigeben,
              rowFreigabeZuruecknehmen: this.rowFreigabeZuruecknehmen,
              rowErneutFreigeben: this.rowErneutFreigeben,
            },
          };
        },
        minWidth: 140,
        width: 140,
        pinned: 'right',
      },
    ];
  },
  mounted() {
    if (this.page === 'katalog') {
      const reiseterminkuerzel = this.$route.query.reiseterminkuerzel;
      if (reiseterminkuerzel) {
        this.showReiseterminFilterModal = false;
        this.onKatalogFilterAnwenden(reiseterminkuerzel);
        // URL Query entfernen
        this.$router.replace({ query: {} });
      }
    } else if (this.page === 'fit') {
      const vorgangId = this.$route.query.vorgangId;
      if (vorgangId) {
        this.showReiseterminFilterModal = false;
        this.onFitFilterAnwenden({ vorgangId: { eq: parseInt(vorgangId) } });
        // URL Query entfernen
        this.$router.replace({ query: {} });
      }
    }
  },
  computed: {
    ...mapState({
      users: state => state.users['users'],
      ...mapGetters(['currentUserId']),
    }),

    // ==================== Beträge & Währungen ====================
    anzahlGesamtePax() {
      if (!this.currentDisplayedRows || this.currentDisplayedRows.length === 0) return 0;

      const uniqueVorgaenge = new Map();
      this.currentDisplayedRows.forEach(row => {
        if (row.vorgangId && row.blankState === 'Buchung' && !uniqueVorgaenge.has(row.vorgangId)) {
          uniqueVorgaenge.set(row.vorgangId, row.npx || 0);
        }
      });

      return Array.from(uniqueVorgaenge.values()).reduce((sum, npx) => sum + npx, 0);
    },

    hatMehrereWaehrungen() {
      if (!this.currentDisplayedRows || this.currentDisplayedRows.length === 0) return false;

      const waehrungen = new Set(this.currentDisplayedRows.map(row => row.waehrung).filter(Boolean));
      return waehrungen.size > 1;
    },

    waehrung() {
      if (!this.currentDisplayedRows || this.currentDisplayedRows.length === 0) return '';
      if (this.hatMehrereWaehrungen) return '';
      const rowMitWaehrung = this.currentDisplayedRows.find(row => row.waehrung);
      return rowMitWaehrung?.waehrung || '';
    },

    gesamtBetrag() {
      if (!this.currentDisplayedRows || this.currentDisplayedRows.length === 0) return 0;
      if (this.hatMehrereWaehrungen) return null;

      return this.currentDisplayedRows.reduce((sum, row) => sum + (row.betrag || 0), 0);
    },

    betraegeProVorgang() {
      if (!this.currentDisplayedRows || this.currentDisplayedRows.length === 0) return [];

      const vorgangMap = new Map();

      this.currentDisplayedRows.forEach(row => {
        const bnr = row.bnr;
        if (!bnr) return;

        // packt die Bnr in die Map ( jede Bnr nur 1 mal ) -> neuer eintrag
        if (!vorgangMap.has(bnr)) {
          vorgangMap.set(bnr, { bnr, betrag: 0, waehrung: row.waehrung });
        }

        // addiert die Beträge der Vorgänge
        const vorgang = vorgangMap.get(bnr);
        vorgang.betrag += row.betrag || 0;
      });

      return Array.from(vorgangMap.values());
    },

    gesamtBetragTooltip() {
      if (!this.betraegeProVorgang || this.betraegeProVorgang.length === 0) return '';
      // wird dann nur angezeigt, wenn es nur 1 Währung gibt
      if (this.hatMehrereWaehrungen) return '';

      // formattiert die Daten als HTML-Tabelle
      const rows = this.betraegeProVorgang
        .map(vorgang => {
          const formattedBetrag = vorgang.betrag.toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          return `
          <tr>
            <td>${vorgang.bnr}:</td>
            <td style="text-align: right; padding-left: 8px">
              ${formattedBetrag} ${vorgang.waehrung || ''}
            </td>
          </tr>`;
        })
        .join('');

      return `<table>${rows}</table>`;
    },

    offenerBetrag() {
      if (!this.currentDisplayedRows || this.currentDisplayedRows.length === 0) return 0;
      if (this.hatMehrereWaehrungen) return null;

      const gesamtbetrag = this.gesamtBetrag;
      const freigabebetragSumme = this.currentDisplayedRows.reduce(
        (sum, row) => sum + (row.freigabebetrag || 0),
        0
      );

      return gesamtbetrag - freigabebetragSumme;
    },

    // ==================== Auswahl Anzeige ====================
    aktuellerReisetermin() {
      if (this.page === 'fit') {
        if (this.selectedIndividualreisen.length === 0) return;
        return this.selectedIndividualreisen[0].reisekuerzel;
      }

      if (!this.selectedReisetermin) return;

      const kuerzel = this.selectedReisetermin.reiseterminkuerzel?.substring(0, 6);
      const startdatum = this.selectedReisetermin.startdatum;
      if (!startdatum) return kuerzel;

      const formattedDate = format(parseISO(startdatum), 'dd.MM.yyyy');
      return `${kuerzel} ${formattedDate}`;
    },

    // ==================== Selektion & Freigabe ====================

    showMultiSelectedActionAvailable() {
      const firstSelectedKreditor = this.allSelectedRows[0]?.data?.kreditor;
      return (
        this.allSelectedRows.length > 1 &&
        firstSelectedKreditor &&
        this.allSelectedRows.every(row => row.data?.kreditor === firstSelectedKreditor)
      );
    },

    leistungenSindFreigebbar() {
      return this.allSelectedRows.every(row => {
        if (istNurLesbar(row.data)) return false;
        const leistungstyp = row.data?.leistungstyp;
        const isStornoOrTeilstorno = leistungstyp === 'Storno' || leistungstyp === 'Teilstorno';
        if (isStornoOrTeilstorno) {
          return row.data?.isFreigegeben === false && (row.data?.betrag ?? 0) !== 0;
        }
        return row.data?.isFreigegeben === false;
      });
    },

    leistungenSindZuruecknehmbar() {
      if (this.allSelectedRows.length === 0) return false;

      // Rechnungsnummer ist ein Array -> inhaltlich vergleichen statt per Referenz
      const ersteRechnungsnummer = JSON.stringify(this.allSelectedRows[0].data?.rechnungsnummer ?? null);

      return this.allSelectedRows.every(
        row =>
          !istNurLesbar(row.data) &&
          row.data?.isFreigegeben === true &&
          row.data?.rechnungsnummer?.length > 0 &&
          JSON.stringify(row.data.rechnungsnummer) === ersteRechnungsnummer
      );
    },
  },
  methods: {
    formatDateWithoutHours,
    getRowId(params) {
      return `${params.data.id}`;
    },
    // Erhaltene Zeilen stornierter Vorgänge sind nur lesbar und lassen sich nicht markieren
    isRowSelectable(node) {
      return !node.data?.isSubRow && !istNurLesbar(node.data);
    },
    getUserNameById(userId) {
      if (!userId) return '-';
      const user = this.users.find(u => u.id === userId);
      return user ? parseFullUsername(user) : '-';
    },

    // ==================== Filter Modal ====================
    openFilterModal() {
      this.isInitialLoad = false;
      this.showReiseterminFilterModal = true;
    },

    async onFilterAnwenden(filterValue) {
      if (this.page === 'fit') {
        await this.onFitFilterAnwenden({ tourId: { eq: filterValue } });
      } else {
        await this.onKatalogFilterAnwenden(filterValue);
      }
    },

    async onKatalogFilterAnwenden(reiseterminkuerzel) {
      try {
        const response = await apiService.get(
          `Reisetermin?$filter=reiseterminkuerzel eq '${reiseterminkuerzel}'&$select=id,reiseterminkuerzel,startdatum,durchfuehrungUnterMtzDurchSkr&$expand=comments($filter=contains(text,'@Buchhaltung'))`
        );

        if (response.data.result.value && response.data.result.value.length > 0) {
          this.selectedReisetermin = response.data.result.value[0];
          this.resetFilterAndReloadGrid();
        } else {
          console.error('Kein Reisetermin gefunden mit Kürzel:', reiseterminkuerzel);
        }
      } catch (error) {
        console.error('Fehler beim Laden des Reisetermins:', error);
      }
    },

    async onFitFilterAnwenden(filter) {
      try {
        const response = await odataService.getIndividualreise({
          filter,
          select: ['tourId', 'reisekuerzel', 'vorgangId'],
        });

        if (response.data && response.data.length > 0) {
          this.selectedIndividualreisen = response.data;
          this.resetFilterAndReloadGrid();
        } else {
          console.error('Keine Individualreise gefunden mit Filter:', filter);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Individualreise:', error);
      }
    },

    resetFilterAndReloadGrid() {
      // Filter-Optionen und angewendete Filter zurücksetzen
      this.allUnfilteredRows = [];
      this.allSelectedRows = [];
      this.selectedRowIds = null;
      this.updateBetragSummeMarkierterLeistungen();
      this.appliedFilterValues = null;
      this.filterValues = {
        kreditor: null,
        leistungskuerzel: null,
        teilleistungskuerzel: null,
        bnr: null,
        rechnungsnummer: null,
        tvzBuchen: null,
      };

      this.isInitialLoad = false;
      this.showReiseterminFilterModal = false;

      this.$nextTick(() => {
        if (this.gridApi) {
          const server = this.server();
          const datasource = this.createDatasource(server);
          this.gridApi.setServerSideDatasource(datasource);
        }
      });
    },

    // ==================== Filter ====================
    onFilterReset() {
      this.appliedFilterValues = null;
      this.filterValues = {
        kreditor: null,
        leistungskuerzel: null,
        teilleistungskuerzel: null,
        bnr: null,
        rechnungsnummer: null,
        tvzBuchen: null,
      };
      this.reloadGrid();
    },

    onToolbarFilterChange(newFilterValues) {
      this.filterValues = { ...newFilterValues };
      this.appliedFilterValues = { ...newFilterValues };
      this.reloadGrid();
    },

    reloadGrid() {
      if (this.gridApi) {
        // Selektierte Row-IDs merken ( zb beim filter wechseln )
        if (!this.selectedRowIds) {
          this.selectedRowIds = new Set();
        }
        this.gridApi.forEachNode(node => {
          if (node.selected) {
            this.selectedRowIds.add(node.id);
          }
        });

        // damit die nicht gemerkten Rows nicht markiert sind
        this.gridApi.deselectAll();

        const server = this.server();
        const datasource = this.createDatasource(server);
        this.gridApi.setServerSideDatasource(datasource);
      }
    },

    // ==================== Grid & Selektion ====================
    onRowSelected(event) {
      this.allSelectedRows = this.getAllSelectedRows();
      // selectedRowIds werden bei row selecten aktulisiert (select oder deselect)
      if (this.selectedRowIds) {
        this.gridApi.forEachNode(node => {
          if (node.selected) {
            this.selectedRowIds.add(node.id);
          } else {
            this.selectedRowIds.delete(node.id);
          }
        });
      }
      this.updateBetragSummeMarkierterLeistungen();

      // Wenn eine Main-Leistung selektiert/deselektiert wurde, müssen die
      // Zellen ihrer Sub-Zeilen ihre cellClassRules anpassen
      const toggledData = event?.data ?? event?.node?.data;
      if (toggledData && !toggledData.isSubRow && toggledData.id != null) {
        const subRowNodes = [];
        this.gridApi.forEachNode(node => {
          if (node.data?.mainLeistungId === toggledData.id) {
            subRowNodes.push(node);
          }
        });
        if (subRowNodes.length > 0) {
          this.gridApi.refreshCells({ rowNodes: subRowNodes, force: true });
        }
      }
    },

    updateBetragSummeMarkierterLeistungen() {
      const statusBarComponent = this.gridApi?.getStatusPanel('repruStatusBar');
      if (!statusBarComponent) return;

      // Sub-Zeilen (Teilbeträge) ausschließen, da der Gruppenbetrag bereits
      // in der Eltern-Leistung enthalten ist (sonst doppelte Summierung)
      const markierteLeistungen = this.allSelectedRows.filter(row => !row.data?.isSubRow);

      if (markierteLeistungen.length === 0) {
        statusBarComponent.setBetragSummeMarkierterLeistungen(null);
        return;
      }

      const summe = markierteLeistungen.reduce((sum, row) => sum + (row.data?.betrag || 0), 0);

      const waehrungen = new Set(markierteLeistungen.map(row => row.data?.waehrung).filter(Boolean));
      const waehrung = waehrungen.size === 1 ? [...waehrungen][0] : '';

      statusBarComponent.setBetragSummeMarkierterLeistungen(summe, waehrung);
    },

    getAllSelectedRows() {
      const rowsSelected = [];
      this.gridApi.forEachNode(node => {
        if (node.selected) {
          rowsSelected.push(node);
        }
      });
      return rowsSelected;
    },

    // ==================== Aufgaben ====================
    onShowModalAufgabeEdit(prefixedVerlinkungen) {
      this.prefixedVerlinkungen = prefixedVerlinkungen;
      this.$bvModal.show('modalAufgabeEdit');
    },

    openAufgabenModalWithData() {
      let idCounter = 0;
      const verlinkungen = [];

      const uniqueVorgangIds = new Set();
      this.allSelectedRows.forEach(row => {
        if (row.data.vorgangId) {
          uniqueVorgangIds.add(row.data.vorgangId);
        }
      });

      // Füge den aktuellen Reisetermin hinzu
      if (this.selectedReisetermin) {
        verlinkungen.push({
          id: idCounter++,
          typ: 'Reisetermin',
          artifactId: this.selectedReisetermin.id,
          artifactText: this.selectedReisetermin.reiseterminkuerzel,
        });
      }

      // Füge alle unique Vorgänge hinzu
      uniqueVorgangIds.forEach(vorgangId => {
        verlinkungen.push({
          id: idCounter++,
          typ: 'Vorgang',
          artifactId: vorgangId,
        });
      });

      // Füge alle ausgewählten Leistungen hinzu
      this.allSelectedRows.forEach(row => {
        if (row.data.id) {
          verlinkungen.push({
            id: idCounter++,
            typ: 'Leistung',
            artifactId: row.data.id,
            artifactText: row.data.leistungskuerzel,
            betrag: row.data.betrag,
            waehrung: row.data.waehrung,
            kreditor: row.data.kreditor,
          });
        }
      });

      this.prefixedVerlinkungen = verlinkungen;
      this.$bvModal.show('modalAufgabeEdit');
    },

    // ==================== Freigabe ====================
    updateLeistungenInGrid(result, isFreigegeben) {
      this.gridApi.forEachNode(rowNode => {
        const matchingLeistung = result.find(item => item.id === rowNode.data.id);

        if (matchingLeistung) {
          const {
            freigabebetrag,
            freigabedatum,
            rechnungsnummer,
            rechnungsdatum,
            freigabegrund,
            freigaben,
            freigabeZurueckgenommenDurch,
            freigabeZurueckgenommenAm,
          } = matchingLeistung;
          const updated = {
            ...rowNode.data,
            freigabebetrag,
            freigabedatum,
            freigegebenVon: isFreigegeben ? this.currentUserId : null,
            rechnungsnummer,
            rechnungsdatum,
            freigabegrund,
            freigaben,
            isFreigegeben,
            freigabeZurueckgenommenDurch,
            freigabeZurueckgenommenAm,
          };
          rowNode.setData(updated);

          // Auch currentDisplayedRows aktualisieren, damit der offene Betrag berechnet wird
          const displayedRowIndex = this.currentDisplayedRows.findIndex(
            row => row.id === matchingLeistung.id
          );
          if (displayedRowIndex !== -1) {
            this.$set(this.currentDisplayedRows, displayedRowIndex, updated);
          }

          // Auch allUnfilteredRows aktualisieren
          const unfilteredRowIndex = this.allUnfilteredRows.findIndex(row => row.id === matchingLeistung.id);
          if (unfilteredRowIndex !== -1) {
            this.$set(this.allUnfilteredRows, unfilteredRowIndex, updated);
          }
        }
      });

      this.gridApi.refreshCells({ columns: ['leistungskuerzel'], force: true });
      this.updateStatusBar();
    },

    // Das Modal bleibt bei einem Fehler offen, damit die Eingaben erhalten bleiben
    async handleFreigabe(data) {
      this.isFreigabeSaving = true;
      try {
        const requestData = data.leistungen.map(leistung => ({
          leistungId: leistung.id,
          bnr: leistung.bnr,
          rechnungsnummer: data.rechnungsnummer,
          rechnungsdatum: data.rechnungsdatum,
          // Manuell gewählter Freigabebetrag bei Gruppen (sonst null -> Backend nutzt vollen Betrag)
          ...(data.freigabebetrag != null && { freigabebetrag: data.freigabebetrag }),
          // Optionaler Kommentar, nur wenn ausgefüllt
          ...(data.kommentar && { kommentar: data.kommentar }),
          // Grund ist nur bei einem Rechnungsdatum ab heute + 7 Tagen Pflicht
          ...(data.freigabegrund && { grund: data.freigabegrund }),
        }));

        const res = await apiService.put('Vorgang/leistung/freigeben', requestData);
        this.updateLeistungenInGrid(res.data.result.value, true);
        this.toast('Die Leistung wurde erfolgreich freigegeben.');
        this.gridApi?.deselectAll();
        this.showFreigabeModal = false;
      } catch (error) {
        // Die Meldung des Backends zeigt der globale Fehlerhandler als Toast an
        console.error('Fehler beim Freigeben der Leistungen:', error);
      } finally {
        this.isFreigabeSaving = false;
      }
    },

    rowFreigeben(leistung) {
      this.selectedLeistungenForFreigabe = [leistung];
      this.showFreigabeModal = true;
    },

    multipleLeistungenFreigeben() {
      if (this.allSelectedRows.length === 0) return;

      this.selectedLeistungenForFreigabe = this.allSelectedRows.map(row => {
        const { id, bnr, betrag, waehrung } = row.data;
        return { id, bnr, betrag, waehrung };
      });
      this.showFreigabeModal = true;
    },

    // ==================== Erneut Freigabe ====================
    rowErneutFreigeben(leistung) {
      this.selectedLeistungenForErneutFreigabe = [leistung];
      this.showErneutFreigabeModal = true;
    },

    // Das Modal bleibt bei einem Fehler offen, damit die Eingaben erhalten bleiben
    async handleErneutFreigabe(data) {
      this.isFreigabeSaving = true;
      try {
        const leistung = data.leistungen[0];
        const res = await apiService.put('Vorgang/leistung/erneutFreigeben', {
          bnr: leistung.bnr,
          leistungId: leistung.id,
          rechnungsnummer: data.rechnungsnummer,
          rechnungsdatum: data.rechnungsdatum,
          // Manuell gewählter Freigabebetrag bei Gruppen (sonst null -> Backend nutzt vollen Betrag)
          ...(data.freigabebetrag != null && { freigabebetrag: data.freigabebetrag }),
          // Optionaler Kommentar, nur wenn ausgefüllt
          ...(data.kommentar && { kommentar: data.kommentar }),
          // Grund ist nur bei einem Rechnungsdatum ab heute + 7 Tagen Pflicht
          ...(data.freigabegrund && { grund: data.freigabegrund }),
        });
        this.updateLeistungenInGrid([res.data.result.value], true);
        this.toast('Die Leistung wurde erfolgreich erneut freigegeben.');
        this.gridApi?.deselectAll();
        this.showErneutFreigabeModal = false;
      } catch (error) {
        // Die Meldung des Backends zeigt der globale Fehlerhandler als Toast an
        console.error('Fehler beim erneuten Freigeben der Leistung:', error);
      } finally {
        this.isFreigabeSaving = false;
      }
    },

    // ==================== Freigabe Zurücknehmen ====================
    async handleFreigabeZuruecknehmen(data) {
      try {
        const requestData = data.leistungen.map(leistung => ({
          leistungId: leistung.id,
          bnr: leistung.bnr,
        }));

        const res = await apiService.put('Vorgang/leistung/freigebenReset', requestData);
        this.updateLeistungenInGrid(res.data.result.value, false);
        this.toast('Die Freigabe der Leistung wurde erfolgreich zurückgenommen.');
        this.gridApi?.deselectAll();
      } catch (error) {
        this.toast('Fehler beim zurücknehmen der Leistung', { variant: 'danger' });
        console.error('Fehler beim Zurücknehmen der Freigabe:', error);
      }
    },

    rowFreigabeZuruecknehmen(leistung) {
      this.selectedLeistungenForFreigabeZuruecknehmen = [leistung];
      this.showFreigabeZuruecknehmenModal = true;
    },

    multipleLeistungenFreigabeZuruecknehmen() {
      if (this.allSelectedRows.length === 0) return;

      this.selectedLeistungenForFreigabeZuruecknehmen = this.allSelectedRows.map(row => {
        const { id, bnr, freigabebetrag, waehrung, rechnungsnummer, freigaben } = row.data;
        return { id, bnr, freigabebetrag, waehrung, rechnungsnummer, freigaben };
      });
      this.showFreigabeZuruecknehmenModal = true;
    },

    // ==================== Server & Datasource ====================
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;

      // Nur Daten laden, wenn der Start-Modal nicht mehr sichtbar ist
      if (!this.showReiseterminFilterModal) {
        const updateData = () => {
          const server = this.server();
          const datasource = this.createDatasource(server);
          params.api.setServerSideDatasource(datasource);
        };

        updateData();
      }
    },

    createDatasource(server) {
      return {
        getRows: async params => {
          const response = await server.getData(params.request);
          if (response.success) {
            // Sub-Zeilen (Teilbeträge) für die Status-Bar-Berechnungen ausschließen,
            this.currentDisplayedRows = response.rows.filter(row => !row.isSubRow);
            this.updateStatusBar();

            params.success({
              rowData: response.rows,
            });

            // Gemerkte Selektionen wiederherstellen
            if (this.selectedRowIds?.size > 0) {
              this.$nextTick(() => {
                this.gridApi.forEachNode(node => {
                  if (this.selectedRowIds.has(node.id)) {
                    node.setSelected(true);
                  }
                });
              });
            }

            if (response.rows.length === 0) {
              this.gridApi.showNoRowsOverlay();
            } else {
              this.gridApi.hideOverlay();
              this.$nextTick(() => {
                this.gridColumnApi?.autoSizeColumns(['kunde']);
                this.gridColumnApi?.autoSizeColumns(['bnr']);
                this.gridColumnApi?.autoSizeColumns(['betrag']);
              });
            }
          } else {
            params.fail();
          }
        },
      };
    },

    server() {
      return {
        getData: async request => {
          console.log('[Datasource] - rows requested by grid server: ', request);

          // Check ob Auswahl vorhanden ist
          if (this.page === 'fit') {
            if (this.selectedIndividualreisen.length === 0) {
              return { success: true, rows: [] };
            }
          } else {
            if (!this.selectedReisetermin?.id) {
              return { success: true, rows: [] };
            }
          }

          // Baue den Leistungen-Filter basierend auf appliedFilterValues
          // deletedAt und isFreigegeben werden clientseitig ausgewertet, im Serverfilter sind sie zu teuer
          const filterBedingungen = [];

          // Odata filter
          if (this.appliedFilterValues) {
            if (this.appliedFilterValues.kreditor) {
              filterBedingungen.push({ kreditor: { eq: this.appliedFilterValues.kreditor } });
            }

            if (this.appliedFilterValues.leistungskuerzel) {
              filterBedingungen.push({ leistungskuerzel: { eq: this.appliedFilterValues.leistungskuerzel } });
            }

            if (this.appliedFilterValues.teilleistungskuerzel) {
              filterBedingungen.push({
                teilleistungskuerzel: { eq: this.appliedFilterValues.teilleistungskuerzel },
              });
            }

            if (this.appliedFilterValues.rechnungsnummer) {
              filterBedingungen.push({
                freigaben: { any: { rechnungsnummer: { eq: this.appliedFilterValues.rechnungsnummer } } },
              });
            }

            if (this.appliedFilterValues.bnr) {
              filterBedingungen.push({
                bnr: { eq: Number(this.appliedFilterValues.bnr) },
              });
            }

            // T-VZ Buchen Filter
            if (this.appliedFilterValues.tvzBuchen === true) {
              filterBedingungen.push({ aufgaben: { any: { kategorie: { eq: 'TvzBuchen' } } } });
            } else if (this.appliedFilterValues.tvzBuchen === false) {
              filterBedingungen.push({ aufgaben: { all: { kategorie: { ne: 'TvzBuchen' } } } });
            }
          }

          let response;

          if (this.page === 'fit') {
            // FIT Page: Lade Vorgang über bnr (= vorgangId aus Individualreise)
            const vorgangIds = this.selectedIndividualreisen.map(
              individualreise => individualreise.vorgangId
            );

            // Aufgaben + Freigaben expand auf der Leistung hinzufügen
            const leistungenExpand = {
              expand: {
                aufgaben: { select: ['kategorie'] },
                freigaben: {},
              },
            };
            const leistungenFilterBedingungen = [...filterBedingungen];
            if (leistungenFilterBedingungen.length > 0) {
              leistungenExpand.filter =
                leistungenFilterBedingungen.length === 1
                  ? leistungenFilterBedingungen[0]
                  : { and: leistungenFilterBedingungen };
            }

            response = await odataService.getVorgang({
              filter: {
                blankState: { in: BUCHUNG_BLANK_STATES },
                leistungen: {
                  any: {
                    bnr: { in: vorgangIds },
                  },
                },
              },
              expand: {
                kunde: {
                  expand: {
                    master: { select: ['vorname', 'nachname'] },
                  },
                },
                leistungen: leistungenExpand,
              },
              select: ['id', 'buchungsdatum', 'brutto', 'reisedatum', 'enddatum', 'npx', 'blankState'],
            });
          } else {
            // Katalog Page: Neuer Leistung-Endpunkt
            // Sicherstellen, dass die Leistung zu einem Vorgang gehört,
            // der eine Hotelleistung mit dem passenden Reisetermin hat
            const reiseterminId = parseInt(this.selectedReisetermin.id);
            filterBedingungen.push({
              vorgang: {
                blankState: { in: BUCHUNG_BLANK_STATES },
                hotelleistungen: {
                  any: {
                    isDeleted: false,
                    reiseterminId: reiseterminId,
                  },
                },
              },
            });

            response = await odataService.getLeistung({
              filter: filterBedingungen.length === 1 ? filterBedingungen[0] : { and: filterBedingungen },
              expand: {
                aufgaben: { select: ['kategorie'] },
                freigaben: {},
                vorgang: {
                  select: ['id', 'buchungsdatum', 'brutto', 'reisedatum', 'enddatum', 'npx', 'blankState'],
                  expand: {
                    kunde: {
                      expand: {
                        master: { select: ['vorname', 'nachname'] },
                      },
                    },
                    hotelleistungen: {
                      filter: {
                        isDeleted: false,
                        reiseterminId: reiseterminId,
                      },
                    },
                  },
                },
              },
            });
          }

          let rows = [];

          if (response.data && Array.isArray(response.data)) {
            if (this.page === 'fit') {
              // FIT: Response enthält Vorgänge mit expandierten Leistungen
              response.data.forEach(vorgang => {
                if (vorgang.leistungen && Array.isArray(vorgang.leistungen)) {
                  const vorgangIstStorniert = istVorgangStorniert(vorgang);

                  vorgang.leistungen.forEach(leistung => {
                    // Soft-gelöschte Leistungen bleiben nur bei stornierten Vorgängen sichtbar
                    if (leistung.deletedAt && !vorgangIstStorniert) {
                      return;
                    }

                    // Bei stornierten Vorgängen bleiben Stornozeilen und bereits freigegebene Leistungen sichtbar
                    if (vorgangIstStorniert && !istStornozeile(leistung) && !leistung.isFreigegeben) {
                      return;
                    }

                    // Bei Storno/Teilstorno -> Währung von Leistung mit gleichem Kreditor nehmen
                    let waehrung = leistung.waehrung;
                    const isStornoOrTeilstorno =
                      leistung.leistungstyp === 'Storno' || leistung.leistungstyp === 'Teilstorno';

                    if (isStornoOrTeilstorno && leistung.kreditor) {
                      // Zuerst im eigenen Vorgang suchen
                      const leistungMitGleichemKreditor = vorgang.leistungen.find(
                        standardLeistung =>
                          standardLeistung.waehrung &&
                          standardLeistung.kreditor === leistung.kreditor &&
                          standardLeistung.leistungstyp !== 'Storno' &&
                          standardLeistung.leistungstyp !== 'Teilstorno'
                      );
                      if (leistungMitGleichemKreditor) {
                        waehrung = leistungMitGleichemKreditor.waehrung;
                      } else {
                        // Falls nicht gefunden (z.B. wenn es nur die Storno Zeile gibt), in allen Vorgängen suchen
                        for (const andererVorgang of response.data) {
                          if (!andererVorgang.leistungen) continue;
                          const gefunden = andererVorgang.leistungen.find(
                            standardLeistung =>
                              standardLeistung.waehrung &&
                              standardLeistung.kreditor === leistung.kreditor &&
                              standardLeistung.leistungstyp !== 'Storno' &&
                              standardLeistung.leistungstyp !== 'Teilstorno'
                          );
                          if (gefunden) {
                            waehrung = gefunden.waehrung;
                            break;
                          }
                        }
                      }
                    }

                    rows.push({
                      ...leistung,
                      waehrung: waehrung,
                      vorgangId: vorgang.id,
                      vorgangBuchungsdatum: vorgang.buchungsdatum,
                      vorgangBrutto: vorgang.brutto,
                      npx: vorgang.npx,
                      reisdatum: vorgang.reisedatum,
                      enddatum: vorgang.enddatum,
                      masterKundeVorname: vorgang.kunde?.master?.vorname,
                      masterKundeNachname: vorgang.kunde?.master?.nachname,
                      blankState: vorgang.blankState,
                      tvzBuchen: leistung.aufgaben?.some(a => a.kategorie === 'TvzBuchen'),
                    });
                  });
                }
              });
            } else {
              // Katalog: Response enthält direkt Leistungen mit expandiertem Vorgang
              response.data.forEach(leistung => {
                const vorgang = leistung.vorgang;
                if (!vorgang) return;

                const vorgangIstStorniert = istVorgangStorniert(vorgang);

                // Soft-gelöschte Leistungen bleiben nur bei stornierten Vorgängen sichtbar
                if (leistung.deletedAt && !vorgangIstStorniert) {
                  return;
                }

                // Bei stornierten Vorgängen bleiben Stornozeilen und bereits freigegebene Leistungen sichtbar
                if (vorgangIstStorniert && !istStornozeile(leistung) && !leistung.isFreigegeben) {
                  return;
                }

                let waehrung = leistung.waehrung;
                const isStornoOrTeilstorno =
                  leistung.leistungstyp === 'Storno' || leistung.leistungstyp === 'Teilstorno';

                if (isStornoOrTeilstorno && leistung.kreditor) {
                  const leistungMitGleichemKreditor = response.data.find(
                    other =>
                      other.bnr === leistung.bnr &&
                      other.waehrung &&
                      other.kreditor === leistung.kreditor &&
                      other.leistungstyp !== 'Storno' &&
                      other.leistungstyp !== 'Teilstorno'
                  );
                  if (leistungMitGleichemKreditor) {
                    waehrung = leistungMitGleichemKreditor.waehrung;
                  } else {
                    const gefunden = response.data.find(
                      other =>
                        other.waehrung &&
                        other.kreditor === leistung.kreditor &&
                        other.leistungstyp !== 'Storno' &&
                        other.leistungstyp !== 'Teilstorno'
                    );
                    if (gefunden) {
                      waehrung = gefunden.waehrung;
                    }
                  }
                }

                rows.push({
                  ...leistung,
                  waehrung: waehrung,
                  vorgangId: vorgang.id,
                  vorgangBuchungsdatum: vorgang.buchungsdatum,
                  vorgangBrutto: vorgang.brutto,
                  npx: vorgang.npx,
                  reisdatum: vorgang.reisedatum,
                  enddatum: vorgang.enddatum,
                  masterKundeVorname: vorgang.kunde?.master?.vorname,
                  masterKundeNachname: vorgang.kunde?.master?.nachname,
                  blankState: vorgang.blankState,
                  tvzBuchen: leistung.aufgaben?.some(a => a.kategorie === 'TvzBuchen'),
                });
              });
            }

            // nur beim ersten Laden, wird für die Filter gebraucht
            if (!this.appliedFilterValues) {
              this.allUnfilteredRows = rows;
            }
          }

          rows.sort((a, b) => (Number(a.bnr) || 0) - (Number(b.bnr) || 0));

          this.mainLeistungenRows = rows;
          const displayedRows = this.buildDisplayedRows();

          console.log('[Datasource] - rows returned:', displayedRows);

          return {
            success: true,
            rows: displayedRows,
          };
        },
      };
    },

    onReload() {
      this.gridApi?.onFilterChanged();
    },

    formatBetrag(betrag) {
      if (betrag == null) return null;
      return betrag.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    async stornoFreigabebetragBearbeiten(event) {
      // Nur speichern wenn der Wert geändert wurde (nicht bei Escape)
      if (!event.valueChanged) {
        return;
      }

      // Aktionen-Spalte sofort refreshen, damit der Freigeben-Button aktualisiert wird
      this.gridApi.refreshCells({ rowNodes: [event.node], columns: ['actions'], force: true });

      const stornobetrag = event.data.betrag;
      if (isNaN(stornobetrag)) {
        return;
      }

      try {
        const res = await apiService.put('Vorgang/leistung/stornoBearbeiten', [
          {
            leistungId: event.data.id,
            bnr: event.data.bnr,
            stornobetrag: stornobetrag,
            waehrung: event.data.waehrung,
          },
        ]);
        this.updateBetragInStornoZeile(res);
        this.toast('Der Betrag wurde erfolgreich bearbeitet.');
      } catch (error) {
        this.toast('Fehler beim bearbeiten des Beitrags', { variant: 'danger' });
        console.error('Fehler beim Speichern des Stornobetrags:', error);
      }
    },
    updateBetragInStornoZeile(res) {
      const result = res.data.result.value;
      this.gridApi.forEachNode(rowNode => {
        const matchingLeistung = result.find(item => item.id === rowNode.data.id);

        if (matchingLeistung) {
          const { betrag, waehrung } = matchingLeistung;
          let updated = { ...rowNode.data, betrag, ...(waehrung && { waehrung }) };
          rowNode.updateData(updated);

          // Auch currentDisplayedRows aktualisieren, damit die der gesamtBetrag berechnet wird
          const displayedRowIndex = this.currentDisplayedRows.findIndex(
            row => row.id === matchingLeistung.id
          );
          if (displayedRowIndex !== -1) {
            this.$set(this.currentDisplayedRows, displayedRowIndex, updated);
          }
        }
      });
      this.updateStatusBar();
    },

    canStornoDuplizieren(data) {
      const istStornoOderTeilstorno = data?.leistungstyp === 'Storno' || data?.leistungstyp === 'Teilstorno';
      if (!istStornoOderTeilstorno || !data.isFreigegeben || (data.betrag ?? 0) === 0) {
        return false;
      }

      // Gruppe = Storno-Zeilen desselben Typs für denselben Vorgang + Kreditor
      const gruppe = this.mainLeistungenRows.filter(
        row =>
          !row.isSubRow &&
          row.leistungstyp === data.leistungstyp &&
          row.bnr === data.bnr &&
          row.kreditor === data.kreditor
      );

      const alleFreigegebenUndBefuellt = gruppe.every(
        row => row.isFreigegeben === true && (row.betrag ?? 0) !== 0
      );
      if (!alleFreigegebenUndBefuellt) return false;

      // Nur an der neuesten Zeile anzeigen, damit pro Gruppe nur ein "+" erscheint
      const hoechsteId = Math.max(...gruppe.map(row => Number(row.id) || 0));
      return Number(data.id) === hoechsteId;
    },

    async stornoDuplizieren(data) {
      try {
        await apiService.post('Vorgang/leistung/stornoErstellen', {
          bnr: data.bnr,
          kreditor: data.kreditor,
          leistungstyp: data.leistungstyp,
        });
        this.reloadGrid();
        this.toast('Eine weitere Stornozeile wurde erstellt.');
      } catch (error) {
        this.toast('Fehler beim Erstellen der Stornozeile', { variant: 'danger' });
        console.error('Fehler beim Erstellen der Stornozeile:', error);
      }
    },
    selectRowOnCellClicked(params) {
      if (params.column.colId === 'actions') return;
      if (params.data?.isSubRow) return;
      if (istNurLesbar(params.data)) return;
      params.node.setSelected(!params.node.isSelected());
    },

    // Klappt die Teilbeträge einer Leistung auf/zu - fügt die Sub-Zeilen via applyServerSideTransaction hinzu/entfernt
    toggleExpand(leistungId) {
      if (!this.gridApi) return;

      const mainLeistung = this.mainLeistungenRows.find(r => r.id === leistungId);
      if (!mainLeistung || !hasExpandableSubRows(mainLeistung)) return;

      const subRows = buildSubRowsFor(mainLeistung);
      const wasExpanded = this.expandedLeistungIds.has(leistungId);
      this.expandedLeistungIds = toggleExpandedId(this.expandedLeistungIds, leistungId);

      if (wasExpanded) {
        this.gridApi.applyServerSideTransaction({
          route: [],
          remove: subRows,
        });
      } else {
        const mainNode = this.gridApi.getRowNode(`${leistungId}`);
        const addIndex = mainNode?.rowIndex != null ? mainNode.rowIndex + 1 : undefined;
        this.gridApi.applyServerSideTransaction({
          route: [],
          add: subRows,
          addIndex,
        });
      }
    },

    getRowClass(params) {
      const klassen = [];
      if (params.data?.isSubRow) klassen.push('leistungen-overview__sub-row');
      // Rot markiert, was nur noch lesbar ist - Stornozeilen bleiben normal, sie werden weiter bearbeitet
      if (istNurLesbar(params.data)) klassen.push('leistungen-overview__storno-row');
      return klassen;
    },

    buildDisplayedRows() {
      return buildDisplayedLeistungen(this.mainLeistungenRows, this.expandedLeistungIds);
    },

    updateStatusBar() {
      const statusBarComponent = this.gridApi?.getStatusPanel('repruStatusBar');
      if (!statusBarComponent) return;

      // Individualreise-Daten/Reisetermin
      if (this.page === 'fit' && this.selectedIndividualreisen.length > 0) {
        statusBarComponent.setReiseterminData({
          id: this.selectedIndividualreisen[0].tourId,
          aktuellerReisetermin: this.selectedIndividualreisen[0].tourId,
          durchfuehrungUnterMtzDurchSkr: false,
          isFitPage: true,
        });
      } else if (this.selectedReisetermin) {
        statusBarComponent.setReiseterminData({
          id: this.selectedReisetermin.id,
          aktuellerReisetermin: this.aktuellerReisetermin,
          durchfuehrungUnterMtzDurchSkr: this.selectedReisetermin.durchfuehrungUnterMtzDurchSkr,
        });
      }

      // Anzahl PAX
      statusBarComponent.setAnzahlPax(this.anzahlGesamtePax);

      // Währung
      statusBarComponent.setWaehrung(this.waehrung);

      // Gesamtbetrag
      statusBarComponent.setGesamtBetrag(this.gesamtBetrag);

      // Offener Betrag
      statusBarComponent.setOffenerBetrag(this.offenerBetrag);

      // Tooltip
      statusBarComponent.setGesamtBetragTooltip(this.gesamtBetragTooltip);
    },
  },
};
</script>

<style scoped>
.info-div {
  border: 1px solid var(--border);
  background-color: var(--surface);
  height: 38px;
  border-radius: 5px;
}

::v-deep .ag-header-center .ag-header-cell-label {
  justify-content: center;
}

::v-deep .tooltip-inner {
  text-align: left;
}

::v-deep .leistungen-overview__sub-row {
  animation: leistungen-overview-fade-in 220ms ease-out;
  background-color: #f0f0f0 !important;
}

::v-deep .ag-row.leistungen-overview__sub-row.ag-row-hover,
::v-deep .ag-row.leistungen-overview__sub-row.ag-row-hover::before {
  background-color: #f0f0f0 !important;
}

::v-deep .leistungen-overview__sub-cell--main-selected {
  background-color: #e5f0fb;
}

/* Zeilen stornierter Vorgänge, Hover mit überschrieben, sonst übermalt AG Grid die Farbe */
::v-deep .leistungen-overview__storno-row,
::v-deep .ag-row.leistungen-overview__storno-row.ag-row-hover,
::v-deep .ag-row.leistungen-overview__storno-row.ag-row-hover::before {
  background-color: var(--repru-storno-row-bg) !important;
}

@keyframes leistungen-overview-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
